import { useEffect, useState } from 'react';
import { send } from '@/shared/messages';
import type { Test } from '@/shared/types';
import { Button } from '@/components/Button';
import { Select } from '@/components/Field';

// Test selector for the header. The active test scopes the chapter list (and
// the card/chat surfaces) and is the test new captures attach to. Doubles as
// the management surface: create a new test, rename, or delete the current one.
export function TestPicker({
  activeTestId,
  onChange,
  refreshKey,
  compact = false,
}: {
  activeTestId: string | null;
  // Called when the active test changes (selection, create, delete). The
  // parent persists it via SET_ACTIVE_TEST and refreshes dependent views.
  onChange: (id: string | null) => void;
  refreshKey: number;
  // Narrow surfaces (mobile top bar): show only the active-test selector and
  // hide create/rename/delete — those management actions stay on the wide
  // header where there's room.
  compact?: boolean;
}) {
  const [tests, setTests] = useState<(Test & { chapterCount: number; dueCount: number })[]>([]);
  // Internal bump to reload the list after a mutation that doesn't change the
  // active selection (e.g. rename).
  const [localVersion, setLocalVersion] = useState(0);

  useEffect(() => {
    send({ type: 'LIST_TESTS' }).then((r) => setTests(r.tests));
  }, [refreshKey, localVersion]);

  const active = tests.find((t) => t.id === activeTestId) ?? null;

  async function createTest() {
    const title = window.prompt('New test name (e.g. "S7"):')?.trim();
    if (!title) return;
    const r = await send({ type: 'CREATE_TEST', title });
    onChange(r.test.id);
  }

  async function renameTest() {
    if (!active) return;
    const title = window.prompt('Rename test:', active.title)?.trim();
    if (!title || title === active.title) return;
    await send({ type: 'UPDATE_TEST', testId: active.id, patch: { title } });
    setLocalVersion((v) => v + 1);
  }

  // Deletion is intentionally NOT offered here — it cascades to every chapter,
  // source, summary, and card in the test, so it lives behind the confirm guard
  // in Settings → Tests (TestsManager), not as a one-tap header button.

  const selector = (
    <Select
      size="sm"
      value={activeTestId ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      className={`font-medium text-slate-800${compact ? ' w-full' : ''}`}
      title="Active test — scopes chapters and is where new captures land"
    >
      {tests.length === 0 && <option value="">no tests</option>}
      {tests.map((t) => (
        <option key={t.id} value={t.id}>
          {t.title} ({t.chapterCount})
        </option>
      ))}
    </Select>
  );

  if (compact) return selector;

  return (
    <div className="flex items-center gap-1">
      {selector}
      <Button size="xs" onClick={() => void createTest()} title="New test">
        ＋
      </Button>
      {active && (
        <Button size="xs" onClick={() => void renameTest()} title="Rename test">
          ✎
        </Button>
      )}
    </div>
  );
}
